import { useCallback, useEffect, useRef } from 'react'

/**
 * Manages document scroll locking while a modal is open.
 *
 * Locking the page is a *global* side effect — a single `overflow-y` on
 * `<html>` — so this hook owns three guarantees that callers cannot be trusted
 * to uphold individually:
 *
 * 1. **Release on unmount.** A caller that unmounts while still open (the
 *    common `{isOpen && <Modal/>}` / `if (!isOpen) return null` pattern) would
 *    otherwise leave the page permanently unscrollable. The hook releases its
 *    own lock when it unmounts, so no caller has to remember to.
 * 2. **Reference counting.** With stacked modals, the inner one closing must
 *    not unlock the page beneath the outer one. The style is cleared only when
 *    the LAST holder releases.
 * 3. **Idempotence.** `openModal`/`closeModal` may fire repeatedly for one open
 *    state (an effect re-running on dependency changes), so each hook instance
 *    holds at most one lock.
 *
 * @example
 * ```tsx
 * const { openModal, closeModal } = useModalScroll()
 * useEffect(() => {
 *   if (isOpen) openModal()
 *   else closeModal()
 * }, [isOpen, openModal, closeModal])
 * // No unmount cleanup needed — the hook handles it.
 * ```
 */
/**
 * Modals currently holding the scroll lock. Nested modals (a finder opened from
 * an attendance modal, a detail card opened from the finder) each take a lock,
 * so the document only scrolls again once the last one releases — otherwise
 * closing the inner modal would unlock the still-open outer one.
 *
 * Keyed by hook instance rather than counted, so a caller can only ever release
 * its OWN lock. That matters because several older call sites still use an
 * `if (isOpen) open() else close()` effect, which fires a `closeModal()` on
 * mount-while-closed with no matching `openModal()`. Under a bare counter that
 * stray call would decrement someone else's lock and unlock the page out from
 * under an open modal; here it is a no-op because that instance holds nothing.
 */
const lockHolders = new Set<symbol>()

/**
 * SN-4709 (James, Aug 12) — "it still feels a little weird that it goes right to
 * the top… looking way up top and then back down here is hard."
 *
 * Where the page was when the FIRST lock was taken, so it can be put back when
 * the last one is released.
 *
 * Measured on the Team Balance sheet (Chrome 151, 1440×900): opening a modal
 * from a scrolled row does NOT move the page, and the modal is correctly
 * viewport-centred. What moves the page is what happens WHILE the modal is
 * open — the mutation's refetch re-renders the grid, the trigger button is
 * destroyed and recreated, and focus restoration scrolls the recreated element
 * into view. Sampled across a Send Invoice: scrollY 478 → 273 about 400ms after
 * the click, with the document height unchanged at 1378 throughout (so it is a
 * deliberate scroll, not a clamp), and it sticks after the modal closes. Close
 * the dialog and you are 200px from where you were looking.
 *
 * Restoring here is safe precisely BECAUSE the document is `overflow-y: hidden`
 * for the whole locked window: the user cannot scroll the page during it, so
 * every scroll that happens in that window is programmatic — which is exactly
 * what should be undone.
 */
let lockedScrollY = 0

const syncOverflow = () => {
  document.documentElement.style.overflowY = lockHolders.size ? 'hidden' : ''
}

/** Test-only: drop any leftover locks between cases. */
export const resetModalScrollLock = () => {
  lockHolders.clear()
  lockedScrollY = 0
  syncOverflow()
}

export const useModalScroll = () => {
  // Identity for this hook instance — the key under which it holds its lock.
  const idRef = useRef<symbol>()
  if (!idRef.current) {
    idRef.current = Symbol('modal-scroll-lock')
  }

  const openModal = useCallback(() => {
    // Only the OUTERMOST lock records the position: a nested modal opens over a
    // page that is already frozen, so its "current" scroll is the outer modal's
    // captured one and re-capturing would just copy it.
    if (lockHolders.size === 0) {
      lockedScrollY = window.scrollY
    }
    lockHolders.add(idRef.current as symbol)
    syncOverflow()
  }, [])

  // Idempotent: releasing twice, or releasing without ever having opened, is a
  // no-op rather than something that disturbs another modal's lock — which is
  // also what keeps a stray release from restoring a scroll position it never
  // captured.
  const closeModal = useCallback(() => {
    if (!lockHolders.delete(idRef.current as symbol)) {
      return
    }
    const releasedLast = lockHolders.size === 0
    syncOverflow()
    // Overflow FIRST, then the position: the document has to be scrollable
    // again before it can be scrolled. The equality guard keeps this a no-op in
    // the common case (nothing moved) and in jsdom, where scrollY is always 0.
    if (releasedLast && window.scrollY !== lockedScrollY) {
      window.scrollTo(0, lockedScrollY)
    }
  }, [])

  // Guarantee 1. Belongs here rather than in each consumer's effect: the hook
  // is what knows a lock was taken, so it is the only place that can promise
  // the lock is always given back. Runs on unmount only (empty deps), and is a
  // no-op for an instance that never locked.
  useEffect(() => closeModal, [closeModal])

  return {
    openModal,
    closeModal,
  }
}
